import { Backdrop, CircularProgress } from "@mui/material";
import SearchBar from "../components/SearchBar/index.jsx";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import RequirementListCard from "../components/Requirements/RequirementListCard.jsx";
import { ReactComponent as RequirementsIcon } from "../assets/icons/Requirements/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../assets/icons/Requirements/AddIcon.svg";
import RequirementRejectionModal from "../modals/RequirementRejectionModal/index.jsx";
import RequirementCreationModal from "../modals/RequirementCreationModal/index.jsx";
import RequirementDisplayModal from "../modals/RequirementDisplayModal/index.jsx";
import ProposalsListModal from "../modals/ProposalsListModal/index.jsx";
import RequirementCreationView from "../components/RequirementCreationView/index.jsx";
import RequirementDetailsView from "../components/RequirementDetailsView/index.jsx";
import ProposalsListView from "../components/ProposalsListView/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown/index.jsx";
import styles from "../styles/Requirements.module.css";

const ignoreSearch = () => {};

const canShowBuyerTabs = (currentUser) =>
  Boolean(currentUser) && currentUser?.tipoDeUsuario !== "4";

const canShowCreateButton = (currentUser) =>
  Boolean(currentUser) &&
  currentUser?.tipoDeUsuario !== "4" &&
  currentUser?.tipoDeUsuario !== "12";

const tabClassName = (isActive) =>
  `${styles.requirementsListHeaderTitleContainer} ${
    isActive ? styles.active : ""
  }`;

export const RequirementsHeader = ({
  t,
  navigate,
  showRequirementDetails,
  showRequirementCreation,
  showProposalsList,
  searchQuery,
  onSearchChange,
  currentUser,
  showCreateRequirementButton,
  onCreateRequirement,
}) => {
  const showSearch =
    !showRequirementDetails && !showRequirementCreation && !showProposalsList;
  const showCreate =
    showSearch &&
    canShowCreateButton(currentUser) &&
    showCreateRequirementButton;
  return (
    <div className={styles.header}>
      <div className={styles.languages}>
        <NotificationBell navigate={navigate} />
        <LanguageSelector />
      </div>
      <div className={styles.headerInfoContainer}>
        <div className={styles.headerInfo}>
          <RequirementsIcon />
          <h1 className={styles.title}>{t("pages.requirements.header")}</h1>
        </div>
        {showSearch && (
          <div className={styles.searchSection}>
            <div className={styles.searchBarWrapper}>
              <SearchBar
                placeholder={t("pages.requirements.searchText")}
                value={searchQuery}
                onChange={onSearchChange}
                onSearch={ignoreSearch}
                width="100%"
              />
            </div>
            {showCreate && (
              <div
                className={styles.newRequirementButton}
                onClick={onCreateRequirement}
              >
                <AddIcon />
                <span>{t("pages.requirements.create")}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.filtersContainer}></div>
    </div>
  );
};

const RequirementsTab = ({ label, active, onSelect }) => (
  <div className={tabClassName(active)} onClick={onSelect}>
    <span className={styles.requirementsListHeaderTitle}>{label}</span>
  </div>
);

export const RequirementsListPanel = ({
  t,
  tabIndex,
  onShowAll,
  onShowMine,
  onShowPending,
  currentUser,
  paginatedRequirementsList,
  showCreateRequirementButton,
  isDesktop,
  onViewRequirement,
  onViewProposals,
  onRejectRequirement,
  onApproveRequirement,
  onToggleActive,
  onResultsLimitChange,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const emptyMessageKey = showCreateRequirementButton
    ? "pages.requirements.noResults"
    : "pages.requirements.noResultsNoPermission";
  return (
    <>
      <div className={styles.requirementsListContainer}>
        <div className={styles.requirementsListHeader}>
          <RequirementsTab
            label={t("pages.requirements.header")}
            active={tabIndex === 0}
            onSelect={onShowAll}
          />
          {canShowBuyerTabs(currentUser) && (
            <>
              <RequirementsTab
                label={t("pages.requirements.myRequirements")}
                active={tabIndex === 1}
                onSelect={onShowMine}
              />
              <RequirementsTab
                label={t("pages.requirements.pendingApproval")}
                active={tabIndex === 2}
                onSelect={onShowPending}
              />
            </>
          )}
        </div>
        <div className={styles.requirementsList}>
          {paginatedRequirementsList.length === 0 && (
            <div className={styles.noResults}>
              <p>{t(emptyMessageKey)}</p>
            </div>
          )}
          {paginatedRequirementsList.length > 0 &&
            paginatedRequirementsList.map((requirement, index) => (
              <RequirementListCard
                key={requirement._id || index}
                requirement={requirement}
                index={index}
                tabIndex={tabIndex}
                isDesktop={isDesktop}
                currentUser={currentUser}
                t={t}
                styles={styles}
                onViewRequirement={onViewRequirement}
                onViewProposals={onViewProposals}
                onRejectRequirement={onRejectRequirement}
                onApproveRequirement={onApproveRequirement}
                onToggleActive={onToggleActive}
              />
            ))}
        </div>
      </div>
      <div className={styles.footer}>
        <ResultsDropdown onResultsLimitChange={onResultsLimitChange} />
        <PageSelector
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </>
  );
};

export const RequirementsStage = ({
  showRequirementDetails,
  selectedRequirement,
  currentUser,
  onBack,
  showRequirementCreation,
  categories,
  sectors,
  certifications,
  onCreateRequirement,
  showProposalsList,
  selectedRequirementForProposals,
  list,
}) => {
  if (showRequirementDetails && selectedRequirement && currentUser) {
    return (
      <RequirementDetailsView
        requirement={selectedRequirement}
        currentUser={currentUser}
        onBack={onBack}
      />
    );
  }
  if (showRequirementCreation) {
    return (
      <RequirementCreationView
        categories={categories}
        sectors={sectors}
        originalCertifications={certifications}
        onCreateRequirement={onCreateRequirement}
        onBack={onBack}
      />
    );
  }
  if (showProposalsList) {
    return (
      <ProposalsListView
        requirement={selectedRequirementForProposals}
        onBack={onBack}
      />
    );
  }
  return list;
};

export const RequirementsDialogs = ({
  categories,
  sectors,
  certifications,
  createModalOpen,
  onCloseCreate,
  onCreateRequirement,
  displayModalOpen,
  onCloseDisplay,
  selectedRequirement,
  currentUser,
  proposalsModalOpen,
  selectedRequirementForProposals,
  onCloseProposals,
  rejectConfirmationOpen,
  confirmationDialogRef,
  onCloseReject,
  onConfirmReject,
  isRejecting,
  isLoading,
}) => (
  <>
    <RequirementCreationModal
      categories={categories}
      sectors={sectors}
      originalCertifications={certifications}
      open={createModalOpen}
      onClose={onCloseCreate}
      onCreateRequirement={onCreateRequirement}
    />
    {displayModalOpen && (
      <RequirementDisplayModal
        open={displayModalOpen}
        onClose={onCloseDisplay}
        requirement={selectedRequirement}
        currentUser={currentUser}
      />
    )}
    {proposalsModalOpen && (
      <ProposalsListModal
        requirement={selectedRequirementForProposals}
        onClose={onCloseProposals}
      />
    )}
    {rejectConfirmationOpen && (
      <RequirementRejectionModal
        dialogRef={confirmationDialogRef}
        open={rejectConfirmationOpen}
        onClose={onCloseReject}
        onConfirm={onConfirmReject}
        isLoading={isRejecting}
      />
    )}
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}
      open={isLoading}
    >
      <CircularProgress
        size={60}
        thickness={4}
        sx={{
          color: "#4eabe7",
        }}
      />
    </Backdrop>
  </>
);
